import {
  Avatar,
  Badge,
  Button,
  Flex,
  Heading,
  Image,
  Text,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import packageJson from "../../package.json";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useAccount,
  useMsal,
} from "@azure/msal-react";
import { ConfirmDestructiveActionDialog } from "../components/modals/ConfirmDestructiveActionDialog";
import { useDirty } from "../contexts/DirtyContext";
import { environmentDisplayName } from "../config";

const capitalizedEnvironmentDisplayName = environmentDisplayName
  ? environmentDisplayName.charAt(0).toUpperCase() + environmentDisplayName.slice(1)
  : "";

const isProduction = !capitalizedEnvironmentDisplayName || capitalizedEnvironmentDisplayName === "Production";

export function AppHeader() {
  const { instance } = useMsal();
  const account = useAccount();
  const navigate = useNavigate();
  const [dirty] = useDirty();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const login = useCallback(
    async (path: string) => {
      await instance.loginRedirect();
      navigate(path);
    },
    [instance, navigate],
  );

  const logout = useCallback(
    async (path: string) => {
      await instance.logoutRedirect();
      navigate(path);
    },
    [instance, navigate],
  );

  return (
    <Flex
      alignItems="center"
      bg="brand.white.100"
      boxShadow="sm"
      color="brand.red.100"
      maxW="100%"
      p={3}
      w="100vw"
    >
      <Image
        aria-label="logo"
        cursor="pointer"
        h={8}
        id="logo-header"
        ml={4}
        src="/logo.png"
      />
      <Heading
        alignItems="center"
        as="h1"
        display="flex"
        fontWeight="bold"
        gap={4}
        ml={8}
        mr="auto"
        size="sm"
      >
        <Text>Verduurzamingskompas Back office</Text>
        <Badge borderRadius={8} colorScheme="green" ml={2} px={4} py={1}>
          v{packageJson.version}
        </Badge>
        {!isProduction && (
          <Text color="gray.500" fontSize="sm">
            {capitalizedEnvironmentDisplayName}
          </Text>
        )}
      </Heading>
      <AuthenticatedTemplate>
        <Flex alignItems="center" gap={4} ml={4} mr={4}>
          <Avatar
            cursor="pointer"
            mx={4}
            name={account?.name ?? account?.username}
            onClick={() => navigate("/profile")}
            size="sm"
          />
          <ConfirmDestructiveActionDialog
            isOpen={confirmLogout}
            onConfirm={(confirmed) => {
              setConfirmLogout(false);
              if (confirmed) logout("/");
            }}
            title="Uitloggen"
          >
            <Text>Weet je zeker dat je wilt uitloggen?</Text>
            {dirty && <Text>Niet opgeslagen wijzigingen gaan verloren</Text>}
          </ConfirmDestructiveActionDialog>
          <Button onClick={() => setConfirmLogout(true)}>Uitloggen</Button>
        </Flex>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Button onClick={() => login("/")}>Inloggen</Button>
      </UnauthenticatedTemplate>
    </Flex>
  );
}
